import { Injectable, inject, signal, computed, effect, untracked } from '@angular/core'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { httpResource, HttpResourceRef } from '@angular/common/http'
import { firstValueFrom } from 'rxjs'
import { environment } from '../../environments/environment'
import { ApiResponse } from '../shared/interfaces/api.interface'
import { AuthService } from '../auth/services/auth.service'
import { UserRole } from '../shared/interfaces/user.interface'
import { formatDateToCyprusFormat } from '../shared/utils/date.utils'
import {
  VideoEvent,
  VideoEventsResponse,
  PossessionBucket,
  PossessionTimelineResponse,
  LineupPlayer,
  LineupTeam,
  LineupResponse,
  AveragePositionsResponse,
  calculateRunningScore,
} from '../shared/services/video-events.service'
import {
  MatchAnalysisResponse,
  MatchDetails,
  MatchNavigationData,
  UpdateMatchOffsetsRequest,
} from './interface'
import { seasonForDate } from '../shared/constants/season.constants'

export interface LineupGroup {
  label: string
  players: LineupPlayer[]
}

@Injectable({
  providedIn: 'root',
})
export class MatchService {
  private http = inject(HttpClient)
  private authService = inject(AuthService)
  private baseUrl = environment.apiUrl

  // ---------------------------------------------------------------------------
  // Signal inputs (set by components)
  // ---------------------------------------------------------------------------

  readonly matchId = signal<string>('')

  // ---------------------------------------------------------------------------
  // httpResource instances — match-scoped
  // ---------------------------------------------------------------------------

  readonly matchDetails: HttpResourceRef<MatchDetails | null> = httpResource<MatchDetails | null>(
    () => {
      const matchId = this.matchId()
      if (!matchId) return undefined
      return `${this.baseUrl}/matches/${matchId}`
    },
    {
      defaultValue: null,
      parse: (raw: unknown) => (raw as ApiResponse<MatchDetails>).data,
    },
  )

  /**
   * Guarded read of matchDetails. Zoneless change detection propagates the throw
   * from .value() on an errored resource, which blanks the page, so every reader
   * goes through here.
   */
  readonly details = computed(() =>
    this.matchDetails.hasValue() ? this.matchDetails.value() : null,
  )

  /** The API answers 403 for a match outside the user's team access. */
  readonly matchAccessDenied = computed(() => {
    const status = (this.matchDetails.error() as HttpErrorResponse | undefined)?.status
    return status === 403
  })

  /** Any other failure to load the match itself. */
  readonly matchLoadFailed = computed(() => {
    const err = this.matchDetails.error() as HttpErrorResponse | undefined
    return !!err && err.status !== 403
  })

  readonly matchAnalysis: HttpResourceRef<MatchAnalysisResponse | null> = httpResource<MatchAnalysisResponse | null>(
    () => {
      const matchId = this.matchId()
      const details = this.details()
      if (!matchId || !details) return undefined

      // Only fetch analysis if user has team access
      const teamIds = [details.home_team.id, details.away_team.id]
      if (!this.authService.hasAccessToAnyTeam(teamIds)) return undefined

      return `${this.baseUrl}/matches/${matchId}/analysis`
    },
    {
      defaultValue: null,
      parse: (raw: unknown) => (raw as ApiResponse<MatchAnalysisResponse>).data,
    },
  )

  // ---------------------------------------------------------------------------
  // Fixture-scoped httpResources (derived from matchDetails.fixture_id)
  // ---------------------------------------------------------------------------

  readonly fixtureId = signal<string | null>(null)

  private videoEventsResource = httpResource<VideoEvent[]>(() => {
    const id = this.fixtureId()
    if (!id) return undefined
    return `${this.baseUrl}/fixtures/${id}/video-events`
  }, {
    defaultValue: [],
    parse: (raw: unknown) => calculateRunningScore((raw as ApiResponse<VideoEventsResponse>).data.events),
  })

  private possessionResource = httpResource<PossessionBucket[]>(() => {
    const id = this.fixtureId()
    if (!id) return undefined
    return `${this.baseUrl}/fixtures/${id}/possession-timeline`
  }, {
    defaultValue: [],
    parse: (raw: unknown) => (raw as ApiResponse<PossessionTimelineResponse>).data.buckets ?? [],
  })

  private lineupResource = httpResource<LineupResponse | null>(() => {
    const id = this.fixtureId()
    if (!id) return undefined
    return `${this.baseUrl}/fixtures/${id}/lineup`
  }, {
    defaultValue: null,
    parse: (raw: unknown) => (raw as ApiResponse<LineupResponse>).data,
  })

  private avgPositionsResource = httpResource<AveragePositionsResponse | null>(() => {
    const id = this.fixtureId()
    if (!id) return undefined
    return `${this.baseUrl}/fixtures/${id}/average-positions`
  }, {
    defaultValue: null,
    parse: (raw: unknown) => (raw as ApiResponse<AveragePositionsResponse>).data,
  })

  // ---------------------------------------------------------------------------
  // Computed signals — fixture data
  // ---------------------------------------------------------------------------

  // Guard every fixture-resource read: reading .value() on an errored httpResource
  // throws (and re-throws every change-detection pass in zoneless), which would
  // take down the whole Overview if any of these 404/403/500. defaultValue does
  // not prevent the throw — hasValue() does.
  readonly keyEvents = computed(() => this.videoEventsResource.hasValue() ? this.videoEventsResource.value() : [])
  readonly possessionBuckets = computed(() => this.possessionResource.hasValue() ? this.possessionResource.value() : [])
  readonly possessionLoading = this.possessionResource.isLoading
  /** Re-fetch the lineup.
   *
   *  Needed after a formation is confirmed: each player's recorded_position is
   *  stamped onto the lineup by the server, so the Starting XI list keeps
   *  showing the OLD positions until this runs -- the pitch updates from
   *  savedFormations and the list does not, and the two disagree on screen. */
  reloadLineup(): void {
    this.lineupResource.reload()
  }

  /** Bumped when Edit Offsets saves. Every clip time on the page hangs on the offsets and
   *  video gaps, and an open page keeps the times it loaded -- so CC saved a gap on dev
   *  (07/10/2026) and saw the same clips as before. Lists that carry clip times reload on it. */
  readonly offsetsVersion = signal(0)

  offsetsSaved(): void {
    this.offsetsVersion.update(v => v + 1)
    this.matchDetails.reload()
    this.videoEventsResource.reload()
  }

  readonly lineupData = computed(() => this.lineupResource.hasValue() ? this.lineupResource.value() : null)
  readonly lineupLoading = this.lineupResource.isLoading
  readonly avgPositions = computed(() => this.avgPositionsResource.hasValue() ? this.avgPositionsResource.value() : null)
  readonly homeLineup = computed<LineupTeam | null>(() => this.lineupData()?.home_team ?? null)
  readonly awayLineup = computed<LineupTeam | null>(() => this.lineupData()?.away_team ?? null)

  /** Merged goals + cards + substitutions sorted chronologically for the header. */
  readonly headerEvents = computed(() => {
    const events: { minute: number; player: string; playerOut?: string; assist?: string; side: 'home' | 'away'; type: 'goal' | 'yellow' | 'red' | 'substitution'; penalty?: boolean; ownGoal?: boolean; videoTimeMs?: number }[] = []
    const keyEvts = this.keyEvents()
    const details = this.details()
    if (!details) return events

    // Collect assists keyed by minute+side for pairing with goals
    const assistsByKey = new Map<string, string[]>()

    // Collect SubbedIn events keyed by minute+team for pairing with SubbedOut
    const subbedInByKey = new Map<string, { player: string; side: 'home' | 'away'; minute: number }>()
    const subbedOutByKey = new Map<string, string[]>()

    // First pass: collect assists and substitutions
    for (const e of keyEvts) {
      const minute = Math.floor(e.start_time_ms / 60000) + 1 // the minute being played (4:30 is 5'), as match reports and Goal Analysis say it (03/11/2026)
      const side = e.side as 'home' | 'away'

      if (e.labels?.includes('Assist')) {
        const key = `${minute}_${side}`
        if (!assistsByKey.has(key)) assistsByKey.set(key, [])
        assistsByKey.get(key)!.push(e.player_name || 'Unknown')
      }

      if (e.base_type === 'SUBSTITUTE') {
        const key = `${minute}_${side}`
        if (e.sub_type === 'SUBBED_IN') {
          // Keyed by the event, not the name: two namesakes coming on in the
          // same minute are two substitutions, not one.
          subbedInByKey.set(e.event_key || key + '_' + (e.player_name || ''), { player: e.player_name || 'Unknown', side, minute })
        } else if (e.sub_type === 'SUBBED_OUT') {
          if (!subbedOutByKey.has(key)) subbedOutByKey.set(key, [])
          subbedOutByKey.get(key)!.push(e.player_name || 'Unknown')
        }
      }
    }

    // Second pass: build events
    for (const e of keyEvts) {
      const minute = Math.floor(e.start_time_ms / 60000) + 1
      const side = e.side as 'home' | 'away'

      if (e.base_type === 'SUBSTITUTE') continue

      // An own goal carries OwnGoal alone (no Goal label), tagged to the scorer's side.
      const isOwnGoal = e.labels?.includes('OwnGoal')
      const isGoal = isOwnGoal || e.labels?.some(l => ['Goal', 'GoalPenalty', 'GoalNonPenalty', 'GoalFreeKickShot', 'GoalShotLong', 'GoalCornerCross'].includes(l))
      if (isGoal) {
        const penalty = e.labels?.includes('ShotPenalty') || e.labels?.includes('GoalPenalty') || false
        const actualSide = isOwnGoal ? (side === 'home' ? 'away' : 'home') : side
        const assistKey = `${minute}_${side}`
        const assist = isOwnGoal ? undefined : assistsByKey.get(assistKey)?.shift()
        // A goal marked in Match control knows its side and minute, not its scorer.
        const player = e.base_type === 'GOAL_MARK' ? 'Goal' : (e.player_name || 'Unknown')
        events.push({ minute, player, assist, side: actualSide, type: 'goal', penalty, ownGoal: isOwnGoal || false, videoTimeMs: e.video_time_ms })
        continue
      }

      if (e.labels?.includes('Assist')) continue

      const isYellow = e.labels?.includes('YellowCard')
      const isRed = e.labels?.includes('RedCard')
      if (isYellow || isRed) {
        events.push({ minute, player: e.player_name || 'Unknown', side, type: isRed ? 'red' : 'yellow', videoTimeMs: e.video_time_ms })
      }
    }

    // Pair substitutions: match SubbedIn with SubbedOut by minute+team
    for (const [, sub] of subbedInByKey) {
      const outKey = `${sub.minute}_${sub.side}`
      const outsForTeam = subbedOutByKey.get(outKey)
      const playerOut = outsForTeam?.shift()
      events.push({ minute: sub.minute, player: sub.player, playerOut, side: sub.side, type: 'substitution' })
    }

    events.sort((a, b) => a.minute - b.minute)
    return events
  })

  /** Max game time in ms from the buckets (used to scale X axis). */
  readonly possessionMaxTime = computed(() => {
    const buckets = this.possessionBuckets()
    if (buckets.length === 0) return 90 * 60000
    return Math.max(...buckets.map(b => b.bucket_ms)) + 30000
  })

  /** Single SVG area path for the possession chart.
   *  Above center = home pushing into opponent's half, below = away pushing.
   *  avg_x is already home-relative from the SQL (away events negated there).
   *  A 3-minute rolling average smooths the curve (buckets are 30s each). */
  readonly possessionPath = computed(() => {
    const buckets = this.possessionBuckets()
    if (buckets.length === 0) return ''

    const maxTime = this.possessionMaxTime()
    const toX = (ms: number) => (ms / maxTime) * 1000
    const toY = (avgX: number) => {
      const normalized = avgX / 52.5 // -1 to 1
      return 100 - normalized * 90
    }

    const sorted = [...buckets].sort((a, b) => a.bucket_ms - b.bucket_ms)

    // 3-minute rolling average (6 buckets of 30s)
    const halfWin = 3
    const smoothed = sorted.map((b, i) => {
      const lo = Math.max(0, i - halfWin)
      const hi = Math.min(sorted.length - 1, i + halfWin)
      let sum = 0
      for (let j = lo; j <= hi; j++) sum += sorted[j].avg_x
      return { bucket_ms: b.bucket_ms, avg_x: sum / (hi - lo + 1) }
    })

    let d = `M ${toX(smoothed[0].bucket_ms)} 100`
    for (const b of smoothed) {
      d += ` L ${toX(b.bucket_ms)} ${toY(b.avg_x)}`
    }
    d += ` L ${toX(smoothed[smoothed.length - 1].bucket_ms)} 100 Z`
    return d
  })

  // ---------------------------------------------------------------------------
  // Computed signals — analysis data
  // ---------------------------------------------------------------------------

  readonly analysisData = computed(() => this.matchAnalysis.value()?.data ?? null)
  readonly isAnalysisLoading = this.matchAnalysis.isLoading

  // ---------------------------------------------------------------------------
  // Computed signals — match display
  // ---------------------------------------------------------------------------

  readonly matchDisplayName = computed(() => {
    const details = this.details()
    if (!details) return 'Match'
    return `${details.home_team.name} vs ${details.away_team.name}`
  })

  // ---------------------------------------------------------------------------
  // Computed signals — substitutions
  // ---------------------------------------------------------------------------

  readonly homeSubstitutions = computed(() =>
    this.headerEvents().filter(e => e.type === 'substitution' && e.side === 'home'),
  )
  readonly awaySubstitutions = computed(() =>
    this.headerEvents().filter(e => e.type === 'substitution' && e.side === 'away'),
  )

  // ---------------------------------------------------------------------------
  // Computed signals — lineup / players
  // ---------------------------------------------------------------------------

  readonly homePlayers = computed(() => {
    const lineup = this.homeLineup()
    return lineup ? this.groupLineupByPosition(lineup.starters) : []
  })

  readonly awayPlayers = computed(() => {
    const lineup = this.awayLineup()
    return lineup ? this.groupLineupByPosition(lineup.starters) : []
  })

  // ---------------------------------------------------------------------------
  // Computed signals — access control
  // ---------------------------------------------------------------------------

  /** Whether the current user can view the currently loaded match (has team access). */
  readonly canViewCurrentMatch = computed(() => {
    const user = this.authService.currentUser()
    const details = this.details()
    if (!user || !details) return false
    return this.authService.hasAccessToAnyTeam([details.home_team.id, details.away_team.id])
  })

  /** Whether the current user is SU or ADMIN. */
  readonly isAdmin = computed(() => {
    const user = this.authService.currentUser()
    return user?.role === UserRole.SU || user?.role === UserRole.ADMIN
  })

  /**
   * Whether the current user may create open share links: admins, superusers, directors and
   * coaches -- not players or scouts (CC, 28/09/2026). The server enforces the same rule.
   */
  /**
   * A match with a video but no analysis yet: a Zone 14 recording, a Veo or other link, or a file
   * uploaded from a computer. The page shows the video, Download and Share, and hides the analysis
   * sections -- which would otherwise show empty pitches and zero-filled stats as if they were data.
   */
  readonly videoOnly = computed(() => {
    const m = this.matchDetails.hasValue() ? this.matchDetails.value() : null
    return !!m && !!m.video_source && m.video_source !== 'SCISPORTS' && !m.analysis_ready && !!m.full_match_video_id
  })

  readonly canShare = computed(() => {
    const role = this.authService.currentUser()?.role
    return role === UserRole.SU || role === UserRole.ADMIN || role === UserRole.DIRECTOR || role === UserRole.COACH
  })

  // ---------------------------------------------------------------------------
  // Constructor — effects
  // ---------------------------------------------------------------------------

  constructor () {
    // Drive fixtureId when match details are available
    effect(() => {
      const details = this.details()
      if (details?.fixture_id && details.status === 'PLAYED') {
        untracked(() => {
          this.fixtureId.set(details.fixture_id!)
        })
      }
    })
  }

  // ---------------------------------------------------------------------------
  // Navigation / utility methods
  // ---------------------------------------------------------------------------

  canViewAnalysis (match: MatchNavigationData): boolean {
    const homeTeam = match.home_team || match.homeTeam
    const awayTeam = match.away_team || match.awayTeam

    if (!homeTeam || !awayTeam) return false

    const teamIds = [homeTeam.id, awayTeam.id]
    return this.authService.hasAccessToAnyTeam(teamIds)
  }

  shouldShowAnalysisButton (match: MatchNavigationData): boolean {
    if (match.status !== 'PLAYED' || !match.is_processed) return false
    return this.canViewAnalysis(match)
  }

  async updateMatchOffsets (
    matchId: string,
    payload: UpdateMatchOffsetsRequest,
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.baseUrl}/matches/${matchId}/offsets`,
        payload,
      ),
    )
  }

  // ---------------------------------------------------------------------------
  // Utility methods
  // ---------------------------------------------------------------------------

  formatDate (dateString: string): string {
    return formatDateToCyprusFormat(dateString)
  }

  // Delegates to the app-wide season rule rather than restating it. This used to roll over in
  // August (month >= 7) while season.constants.ts and the season picker roll over on 1 July, so a
  // July fixture was filed under the previous season here and the current one everywhere else.
  //
  // The date arrives as a calendar date ("2026-07-01"). new Date() would read that as UTC midnight,
  // and seasonForDate reads the local month, which west of UTC is still 30 June -- so the parts are
  // taken as a local date instead.
  extractSeason (dateStr: string): string {
    const [year, month, day] = dateStr.slice(0, 10).split('-').map(Number)
    return seasonForDate(new Date(year, month - 1, day))
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Where a recorded position sorts in the lineup list: back to front, and
   * left to right within each line. ST is always last, DM always before AM
   * -- CC's rule, 26/09/2026.
   *
   * A 1-4-3-3 therefore reads GK, LB, LCB, RCB, RB, DM, AM, AM, LW, RW, ST.
   *
   * Wing-backs sit OUTSIDE the centre-backs (LWB before LCB, RWB after RCB):
   * in a back three they are the wide players of that line, so reading left
   * to right puts them on the ends.
   *
   * This is the LIST order and is deliberately not the slot order stored in
   * formation_slot_positions -- slots count from the goalkeeper outward, which
   * is what the pitch geometry needs, and reading a team sheet is a different
   * job from drawing one.
   */
  private static readonly POSITION_SORT: Record<string, number> = {
    GK: 0,
    LWB: 10, LB: 11, LCB: 12, CB: 13, RCB: 14, RB: 15, RWB: 16,
    // Wide midfielders flank the central ones, so a 1-4-4-2 midfield reads
    // LM, DM, DM, RM left to right. They are NOT LW/RW: a wide midfielder has
    // a full-back behind him and defends his flank, a winger is the width of
    // the front line.
    LM: 19, DM: 20, RM: 21,
    AM: 30,
    LW: 40, RW: 41,
    ST: 50,
  }

  private positionSortKey (p: LineupPlayer): number {
    const code = (p.recorded_position ?? '').toUpperCase()
    // Anything without a recorded position sorts after everything that has
    // one, rather than jumping to the front on an unknown code.
    return MatchService.POSITION_SORT[code] ?? 99
  }

  private groupLineupByPosition (players: LineupPlayer[]): LineupGroup[] {
    const order = ['Goalkeeper', 'Defender', 'Midfielder', 'Forward']
    const groups = new Map<string, LineupPlayer[]>()
    for (const p of players) {
      // Group by the position SCIOS recorded where there is one, so the
      // heading agrees with the code shown beside the player and with the
      // pitch. Grouping on p.position (the SciSports label) while displaying
      // recorded_position put Sergiou under "Defenders" with "DM" next to his
      // name -- reported by CC, 25/09/2026.
      const pos = this.normalizePosition(p.recorded_position || p.position || 'Unknown')
      if (!groups.has(pos)) groups.set(pos, [])
      groups.get(pos)!.push(p)
    }
    // Sort within each group so the line reads left to right. Stable, so
    // players the rule cannot rank keep the order the server sent.
    for (const list of groups.values()) {
      list.sort((a, b) => this.positionSortKey(a) - this.positionSortKey(b))
    }
    return order.filter(pos => groups.has(pos)).map(pos => ({ label: pos + 's', players: groups.get(pos)! }))
  }

  private normalizePosition (pos: string): string {
    const p = pos.toLowerCase()
    if (p.includes('goal') || p === 'gk') return 'Goalkeeper'
    // Recorded codes (GK CB LCB RCB LB RB LWB RWB DM AM LW RW ST) are matched
    // here alongside the longer SciSports names. Wing-backs are defenders: in a
    // back three they drop in to make a back five.
    if (p.includes('def') || p.includes('back') || p === 'cb' || p === 'lcb' || p === 'rcb'
        || p === 'lb' || p === 'rb' || p === 'wb' || p === 'lwb' || p === 'rwb') return 'Defender'
    if (p.includes('mid') || p === 'cm' || p === 'lcm' || p === 'rcm' || p === 'dm'
        || p === 'ldm' || p === 'rdm' || p === 'am' || p === 'cdm' || p === 'cam'
        || p === 'lm' || p === 'rm') return 'Midfielder'
    if (p.includes('for') || p.includes('wing') || p.includes('strik') || p === 'cf'
        || p === 'st' || p === 'lst' || p === 'rst' || p === 'lw' || p === 'rw') return 'Forward'
    return 'Midfielder'
  }
}
